import { isEqual } from "lodash";
import {
  AlertTriangle,
  Check,
  Download,
  FolderOpen,
  Loader2,
  RefreshCw,
  Save,
  Search,
  Trash2,
  X,
} from "lucide-react";
import moment from "moment";
import { useCallback, useEffect, useRef, useState } from "react";
import { components, MultiValueProps } from "react-select";
import AsyncSelect from "react-select/async";
import { FlashingValue } from "src/common/FlashingValue";
import { useSocket } from "src/components/SocketContext";
import { ScreenerFilterState } from "src/components/screener/screenerFilter";
import { ScreenerFilterBar } from "src/components/screener/screenerFilterBar";
import ScreenerTableSkeleton from "src/components/skeleton/screenerTableSkeleton";
import { SCREENER_TABLE_COLUMNS } from "src/constants/screenerTableColumn";
import { fetchWrapper } from "src/helpers";
import {
  formatToCr,
  getLocalStorageUser,
} from "src/helpers/api/sharedFunction";
import { getSortIcon, SortConfig } from "src/helpers/api/sortconfig";
import { formatCurrencyWithLocalString } from "src/helpers/numberFormatter";
import { alertService } from "src/services";

export interface StockData {
  symbol: string;
  company_name: string;
  price: number;
  // Latest price and daily change, resolved server-side from the live market
  // feed (falls back to the batch closing price when the feed has no live
  // tick for the symbol yet) — correct even before any socket tick arrives.
  ltp?: number | null;
  change_percent?: number | null;
  volume: string;
  market_cap_in_cr: string;
  pe_ratio: string;
  eps_dil_growth: string;
  dividend_yield: string;
  sector: string;
  industry: string;
  index?: string;
}

interface LiveStockTick {
  ltp: number;
  close: number;
  lastUpdated: Date;
}

type StockUpdateMessage = {
  symbol_name: string;
  ltp: string;
  close: string;
};

export interface SavedScreener {
  id: string;
  name: string;
  criteria?: {
    filterState?: ScreenerFilterState;
    sortConfig?: SortConfig;
  };
}

export default function StockScreener() {
  const { emit, on, off, isConnected } = useSocket();
  const [stocks, setStocks] = useState<StockData[]>([]);
  const [liveTicks, setLiveTicks] = useState<Record<string, LiveStockTick>>({});
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: null,
    direction: null,
  });
  const [filterState, setFilterState] = useState<ScreenerFilterState>({});

  const [savedScreeners, setSavedScreeners] = useState<SavedScreener[]>([]);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [screenerName, setScreenerName] = useState("");
  const [isLoadDropdownOpen, setIsLoadDropdownOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isFetchingScreeners, setIsFetchingScreeners] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [screenerToDelete, setScreenerToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [activeScreener, setActiveScreener] = useState<{
    id: string;
    name: string;
    isModified: boolean;
  } | null>(null);
  const [showDiscardModal, setShowDiscardModal] = useState(false);

  const fetchScreeners = async () => {
    setIsFetchingScreeners(true);
    try {
      const user = getLocalStorageUser();
      const res = await fetchWrapper.get("/api/users/screeners", undefined, {
        Authorization: `Bearer ${user?.token}`,
      });
      if (res?.success) {
        setSavedScreeners(res.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch screeners:", error);
    } finally {
      setIsFetchingScreeners(false);
    }
  };

  const handleUpdateScreener = async () => {
    if (!activeScreener) return;
    setIsSaving(true);
    try {
      const user = getLocalStorageUser();
      const criteria = { filterState, sortConfig };
      const res = await fetchWrapper.put(
        `/api/users/screeners?id=${activeScreener.id}`,
        { name: activeScreener.name, criteria },
        { Authorization: `Bearer ${user?.token}` },
      );
      if (res?.success) {
        setActiveScreener({ ...activeScreener, isModified: false });
        fetchScreeners();
        alertService.success("Screener updated successfully");
      } else {
        alertService.error(res?.message || "Failed to update screener");
      }
    } catch (error) {
      console.error("Error updating screener:", error);
      alertService.error(
        (error as Error)?.message || "An error occurred while updating.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveScreener = async () => {
    if (!screenerName.trim()) return;
    setIsSaving(true);
    try {
      const user = getLocalStorageUser();
      const criteria = { filterState, sortConfig };
      const res = await fetchWrapper.post(
        "/api/users/screeners",
        { name: screenerName, criteria },
        { Authorization: `Bearer ${user?.token}` },
      );
      if (res?.success) {
        setIsSaveModalOpen(false);
        setActiveScreener({
          id: res.data.id,
          name: screenerName,
          isModified: false,
        });
        setScreenerName("");
        fetchScreeners();
        alertService.success("Screener saved successfully");
      } else {
        alertService.error(res?.message || "Failed to save screener");
      }
    } catch (error) {
      console.error("Error saving screener:", error);
      alertService.error(
        (error as Error)?.message || "An error occurred while saving.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteScreener = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const user = getLocalStorageUser();
      const res = await fetchWrapper.delete(
        `/api/users/screeners?id=${id}`,
        undefined,
        { Authorization: `Bearer ${user?.token}` },
      );
      if (res?.success) {
        fetchScreeners();
        alertService.success("Screener deleted successfully");
        if (activeScreener && activeScreener.id === id) {
          setActiveScreener(null);
          setFilterState({});
          setSortConfig({ key: null, direction: null });
        }
      } else {
        alertService.error("Failed to delete screener");
      }
    } catch (error) {
      console.error("Error deleting screener:", error);
      alertService.error("An error occurred while deleting.");
    }
  };

  const handleLoadScreener = (screener: {
    id: string;
    name: string;
    criteria?: { filterState?: ScreenerFilterState; sortConfig?: SortConfig };
  }) => {
    setFilterState(screener.criteria?.filterState || {});
    if (screener.criteria?.sortConfig) {
      setSortConfig(screener.criteria.sortConfig);
    }
    setActiveScreener({
      id: screener.id,
      name: screener.name,
      isModified: false,
    });
    setIsLoadDropdownOpen(false);
  };
  const [totalItems, setTotalItems] = useState(0);
  const [lastUpdatedDate, setLastUpdatedDate] = useState<string | null>(null);
  const [isSymbolSearchOpen, setIsSymbolSearchOpen] = useState(false);

  const CustomMultiValue = (
    props: MultiValueProps<{ label: string; value: string }, true>,
  ) => {
    const { index, getValue } = props;
    const values = getValue();
    const maxVisible = 3;

    if (index < maxVisible) {
      return <components.MultiValue {...props} />;
    }
    if (index === maxVisible) {
      return (
        <div className="text-[10px] text-gray-500 dark:text-gray-400 font-medium px-1 bg-gray-100 dark:bg-gray-800 rounded mx-0.5 whitespace-nowrap self-center">
          +{values.length - maxVisible}
        </div>
      );
    }
    return null;
  };

  const observer = useRef<IntersectionObserver | null>(null);
  const lastStockElementRef = useCallback(
    (node: HTMLTableRowElement | null) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prevPage) => prevPage + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore],
  );

  const handleSort = (key: string) => {
    setSortConfig((prevSort) => {
      let direction: "asc" | "desc" | null = "desc";
      if (prevSort.key === key && prevSort.direction === "desc") {
        direction = "asc";
      } else if (prevSort.key === key && prevSort.direction === "asc") {
        direction = null;
      }
      const newSort = { key: direction ? key : null, direction };

      setActiveScreener((prevActive) => {
        if (!prevActive) return null;
        const originalScreener = savedScreeners.find(
          (s) => s.id === prevActive.id,
        );
        if (!originalScreener) return { ...prevActive, isModified: true };

        const originalFilters = originalScreener.criteria?.filterState || {};
        const originalSort = originalScreener.criteria?.sortConfig || {
          key: null,
          direction: null,
        };

        const isModified =
          !isEqual(filterState, originalFilters) ||
          !isEqual(newSort, originalSort);
        return { ...prevActive, isModified };
      });

      return newSort;
    });
    setPage(1);
    setHasMore(true);
    setStocks([]);
  };

  useEffect(() => {
    setPage(1);
    setHasMore(true);
    setStocks([]);
    setTotalItems(0);
  }, [filterState]);

  useEffect(() => {
    const fetchStocks = async () => {
      try {
        setLoading(true);
        let url = `/api/screener?page=${page}&limit=20`;
        if (sortConfig.key) {
          url += `&sortColumn=${sortConfig.key}&sortOrder=${sortConfig.direction}`;
        }

        const filterEntries = Object.entries(filterState);
        if (filterEntries.length > 0) {
          const filterParams = new URLSearchParams();
          filterEntries.forEach(([key, filterVal]) => {
            if (filterVal.type === "toggle") {
              filterParams.append(key, "true");
            } else if (filterVal.type === "userInput") {
              if (filterVal.operator === "between") {
                filterParams.append(
                  `greater_than_equal[${key}]`,
                  filterVal.value,
                );
                if (filterVal.maxValue) {
                  filterParams.append(
                    `less_than_equal[${key}]`,
                    filterVal.maxValue,
                  );
                }
              } else {
                const op = filterVal.operator || "greater_than_equal";
                filterParams.append(`${op}[${key}]`, filterVal.value);
              }
            } else if (filterVal.type === "select") {
              filterParams.append(key, filterVal.value.join(","));
            } else if (filterVal.type === "selectWithInput") {
              filterParams.append(
                `${filterVal.selection}[${key}]`,
                filterVal.value,
              );
            } else if (filterVal.type === "selectWithSelect") {
              filterParams.append(filterVal.selection, filterVal.value);
            }
          });
          url += `&${filterParams.toString()}`;
        }

        const data = await fetchWrapper.get(url);

        if (data && data.data && data.data.length > 0) {
          setTotalItems(data.pagination?.totalItems || 0);
          if (data.lastUpdatedDate) setLastUpdatedDate(data.lastUpdatedDate);
          setStocks((prev) =>
            page === 1 ? data.data : [...prev, ...data.data],
          );
          if (
            data.data.length < 20 ||
            data.hasMore === false ||
            data.nextPage === null
          ) {
            setHasMore(false);
          } else {
            setHasMore(true);
          }
        } else {
          if (page === 1) setStocks([]);
          setHasMore(false);
        }
      } catch (error) {
        console.error("Error fetching stocks:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStocks();
  }, [page, sortConfig, filterState]);

  const handleStockUpdate = useCallback((message: StockUpdateMessage[]) => {
    setLiveTicks((prev) => {
      const next = { ...prev };
      message.forEach((tick) => {
        const ltp = parseFloat(tick.ltp);
        const close = parseFloat(tick.close);
        if (!Number.isNaN(ltp)) {
          next[tick.symbol_name] = { ltp, close, lastUpdated: new Date() };
        }
      });
      return next;
    });
  }, []);

  // Subscribe to live price ticks for every symbol currently loaded on screen.
  const loadedSymbolsKey = stocks.map((stock) => stock.symbol).join(",");
  useEffect(() => {
    if (!isConnected || !loadedSymbolsKey) return;
    const symbols = loadedSymbolsKey.split(",");

    on("stock_update", handleStockUpdate);
    emit("subscribe_stock", { symbol: symbols });

    return () => {
      off("stock_update", handleStockUpdate);
      emit("unsubscribe_stock", { symbol: symbols });
    };
  }, [loadedSymbolsKey, isConnected, on, off, emit, handleStockUpdate]);

  const exportCSV = async () => {
    if (stocks.length === 0) return;
    try {
      let url = `/api/screener/export?`;
      if (sortConfig.key) {
        url += `sortColumn=${sortConfig.key}&sortOrder=${sortConfig.direction}&`;
      }

      const filterEntries = Object.entries(filterState);
      const filterParams = new URLSearchParams();
      if (filterEntries.length > 0) {
        filterEntries.forEach(([key, filterVal]) => {
          if (filterVal.type === "toggle") {
            filterParams.append(key, "true");
          } else if (filterVal.type === "userInput") {
            if (filterVal.operator === "between") {
              filterParams.append(
                `greater_than_equal[${key}]`,
                filterVal.value,
              );
              if (filterVal.maxValue) {
                filterParams.append(
                  `less_than_equal[${key}]`,
                  filterVal.maxValue,
                );
              }
            } else {
              const op = filterVal.operator || "greater_than_equal";
              filterParams.append(`${op}[${key}]`, filterVal.value);
            }
          } else if (filterVal.type === "select") {
            filterParams.append(key, filterVal.value.join(","));
          } else if (filterVal.type === "selectWithInput") {
            filterParams.append(
              `${filterVal.selection}[${key}]`,
              filterVal.value,
            );
          } else if (filterVal.type === "selectWithSelect") {
            filterParams.append(filterVal.selection, filterVal.value);
          }
        });
      }
      url += filterParams.toString();

      const user = getLocalStorageUser();
      const headers: Record<string, string> = {};
      if (user?.token) {
        headers["Authorization"] = `Bearer ${user.token}`;
      }

      const response = await fetch(url, { headers });
      if (!response.ok) throw new Error("Export failed");

      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;

      const contentDisposition = response.headers.get("content-disposition");
      let filename = "screener_export.xlsx";
      if (contentDisposition && contentDisposition.includes("filename=")) {
        filename = contentDisposition.split("filename=")[1].replace(/"/g, "");
      }

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Error exporting CSV:", error);
    }
  };

  const loadOptions = async (inputValue: string) => {
    if (!inputValue) return [];
    try {
      const user = getLocalStorageUser();
      const response = await fetchWrapper.get(
        "/api/dashboard/company_info?name=" + encodeURIComponent(inputValue),
        undefined,
        { Authorization: `Bearer ${user?.token}` },
      );
      if (response && response.data) {
        const uniqueStocks = Array.from(
          new Map(
            response.data.map(
              (stock: { symbol: string; company_name: string }) => [
                stock.symbol,
                stock,
              ],
            ),
          ).values(),
        ) as { symbol: string; company_name: string }[];
        return uniqueStocks.map((stock) => ({
          label: `${stock.symbol} - ${stock.company_name}`,
          value: stock.symbol,
        }));
      }
      return [];
    } catch {
      return [];
    }
  };

  const customStyles = {
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: () => ({ display: "none" }),
    menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 }),
  };

  return (
    <div className="w-full text-black dark:text-white flex flex-col h-[calc(100vh-120px)] overflow-hidden bg-white dark:bg-[#111322] rounded-md border border-gray-200 dark:border-gray-800">
      {/* Header Section */}
      <div className="px-4 py-2 flex flex-col md:flex-row justify-between items-start md:items-center gap-2 border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-center gap-4 flex-1 w-full flex-wrap sm:flex-nowrap">
          <h1 className="text-xl font-bold flex items-center gap-2 text-gray-800 dark:text-gray-100 whitespace-nowrap">
            Stock Screener
            {activeScreener && (
              <span className=" text-xs font-normal text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-1 rounded ml-2 border border-blue-200 dark:border-blue-800/50 flex items-center gap-1">
                {activeScreener.name}{" "}
                {activeScreener.isModified && (
                  <span className="text-gray-500 italic">(Modified)</span>
                )}
                <button
                  onClick={() => {
                    if (activeScreener.isModified) {
                      setShowDiscardModal(true);
                    } else {
                      setActiveScreener(null);
                      setFilterState({});
                      setSortConfig({ key: null, direction: null });
                    }
                  }}
                  className="cursor-pointer hover:bg-blue-200 dark:hover:bg-blue-800 rounded-full p-0.5 transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
          </h1>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex flex-col items-end text-[10px] text-gray-500 dark:text-gray-400">
            <span>
              Last updated on:{" "}
              <span className="font-medium text-gray-700 dark:text-gray-300">
                {lastUpdatedDate
                  ? moment(lastUpdatedDate).format("DD MMM YYYY")
                  : "-"}
              </span>{" "}
              closing
            </span>
          </div>
          <div className="relative">
            <button
              onClick={() => {
                setIsLoadDropdownOpen(true);
                fetchScreeners();
              }}
              className="px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
            >
              <FolderOpen className="w-4 h-4" /> Load
            </button>
          </div>

          <div className="flex gap-2">
            {activeScreener ? (
              <>
                <button
                  onClick={handleUpdateScreener}
                  disabled={
                    stocks.length === 0 ||
                    !activeScreener.isModified ||
                    isSaving
                  }
                  className="px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RefreshCw
                    className={`w-4 h-4 ${isSaving ? "animate-spin" : ""}`}
                  />{" "}
                  Update
                </button>
                <button
                  onClick={() => setIsSaveModalOpen(true)}
                  disabled={stocks.length === 0}
                  className="px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="w-4 h-4" /> Save As
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsSaveModalOpen(true)}
                disabled={stocks.length === 0}
                className="px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4" /> Save
              </button>
            )}
          </div>

          <button
            onClick={exportCSV}
            disabled={stocks.length === 0}
            className="px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" /> Export Data
          </button>
        </div>
      </div>

      <ScreenerFilterBar
        filterState={filterState}
        onFilterChange={(newFilters) => {
          setFilterState(newFilters);
          setActiveScreener((prevActive) => {
            if (!prevActive) return null;
            const originalScreener = savedScreeners.find(
              (s) => s.id === prevActive.id,
            );
            if (!originalScreener) return { ...prevActive, isModified: true };

            const originalFilters =
              originalScreener.criteria?.filterState || {};
            const originalSort = originalScreener.criteria?.sortConfig || {
              key: null,
              direction: null,
            };

            const isModified =
              !isEqual(newFilters, originalFilters) ||
              !isEqual(sortConfig, originalSort);
            return { ...prevActive, isModified };
          });
        }}
      />

      {/* Data Table */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        <table className="w-full table-fixed text-xs text-left border-collapse">
          <colgroup>
            {SCREENER_TABLE_COLUMNS.map((col) => (
              <col key={col.key} style={{ width: `${col.widthPercent}%` }} />
            ))}
          </colgroup>
          <thead className="text-[11px] text-gray-500 dark:text-gray-400 uppercase bg-gray-50 dark:bg-[#181a2d] border-b border-gray-200 dark:border-gray-800 sticky top-0 z-10">
            <tr>
              {SCREENER_TABLE_COLUMNS.map((col) => (
                <th
                  key={col.key}
                  className={`px-2 pr-5 py-2 font-medium group relative whitespace-nowrap overflow-visible ${
                    col.align === "right" ? "text-right" : "text-left"
                  } ${
                    col.key !== "change_percent" &&
                    (col.key !== "symbol" || !isSymbolSearchOpen)
                      ? "hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
                      : ""
                  }`}
                  onClick={() => {
                    if (col.key === "change_percent") return;
                    if (col.key === "symbol" && isSymbolSearchOpen) return;
                    handleSort(col.key);
                  }}
                >
                  {col.key === "symbol" ? (
                    <div
                      className="flex items-center w-full gap-2 h-[28px]"
                      onClick={(e) => isSymbolSearchOpen && e.stopPropagation()}
                    >
                      {!isSymbolSearchOpen ? (
                        <>
                          <div className="flex items-center gap-1.5 overflow-hidden">
                            <span>{col.label}</span>{" "}
                            {getSortIcon(sortConfig, col.key)}
                            {filterState["symbols"]?.type === "select" &&
                              filterState["symbols"].value.length > 0 && (
                                <span className="text-[10px] bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 px-1.5 py-0.5 rounded font-bold shrink-0">
                                  {filterState["symbols"].value.length}
                                </span>
                              )}
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <Search
                              className="w-3.5 h-3.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                setIsSymbolSearchOpen(true);
                              }}
                            />
                            {filterState["symbols"]?.type === "select" &&
                              filterState["symbols"].value.length > 0 && (
                                <X
                                  className="w-3.5 h-3.5 text-gray-400 hover:text-red-500 transition-colors"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setFilterState((prev) => {
                                      const next = { ...prev };
                                      delete next["symbols"];
                                      return next;
                                    });
                                  }}
                                />
                              )}
                          </div>
                        </>
                      ) : (
                        <div
                          className="w-full relative flex items-center gap-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <AsyncSelect
                            isMulti
                            cacheOptions
                            defaultOptions={false}
                            loadOptions={loadOptions}
                            placeholder="Search symbol..."
                            styles={customStyles}
                            components={{
                              MultiValue: CustomMultiValue,
                              DropdownIndicator: () => null,
                              IndicatorSeparator: () => null,
                              ClearIndicator: () => null,
                            }}
                            classNamePrefix="react-select"
                            className="flex-1"
                            autoFocus
                            classNames={{
                              control: ({ isFocused }) =>
                                `!bg-white dark:!bg-[#1a1d2d] !border ${isFocused ? "!border-blue-500 !ring-1 !ring-blue-500/20" : "!border-gray-200 dark:!border-gray-700"} !shadow-none !h-[28px] !min-h-[28px] !rounded !text-[11px] transition-all`,
                              input: () =>
                                "!text-gray-900 dark:!text-gray-100 !m-0 !p-0",
                              placeholder: () =>
                                "!text-gray-500 dark:!text-gray-400 !text-[11px]",
                              menu: () =>
                                "!bg-white dark:!bg-[#1a1d2d] !border !border-gray-200 dark:!border-gray-800 !shadow-lg !rounded-md !mt-1 !text-xs !font-normal !normal-case",
                              menuList: () => "!p-1",
                              option: ({ isFocused, isSelected }) =>
                                `!cursor-pointer !rounded !px-2 !py-1.5 ${
                                  isSelected
                                    ? "!bg-blue-600 !text-white"
                                    : isFocused
                                      ? "!bg-gray-100 dark:!bg-gray-800/50 !text-gray-900 dark:!text-gray-100"
                                      : "!bg-transparent !text-gray-700 dark:!text-gray-300 hover:!bg-gray-50 dark:hover:!bg-gray-800/50"
                                }`,
                              noOptionsMessage: () =>
                                "!text-gray-500 dark:!text-gray-400 !text-xs !py-1 !px-2",
                              multiValue: () =>
                                "!bg-blue-50 dark:!bg-blue-900/30 !border !border-blue-200 dark:!border-blue-800 !rounded !m-0 !mr-1 !shrink-0 !max-w-[70px] !my-auto",
                              multiValueLabel: () =>
                                "!text-blue-700 dark:!text-blue-300 !text-[10px] !px-1.5 !py-0 truncate font-normal normal-case",
                              multiValueRemove: () =>
                                "!text-blue-500 hover:!bg-blue-100 hover:dark:!bg-blue-800 hover:!text-blue-900 hover:dark:!text-blue-100 !rounded-r !cursor-pointer transition-colors !p-0",
                              valueContainer: () =>
                                "!p-0 !px-1.5 !gap-0 !flex-nowrap overflow-x-hidden font-normal normal-case !items-center",
                              indicatorsContainer: () => "!pr-1",
                            }}
                            menuPortalTarget={
                              typeof document !== "undefined"
                                ? document.body
                                : null
                            }
                            value={(filterState["symbols"]?.type === "select"
                              ? filterState["symbols"].value
                              : []
                            ).map((val) => ({ label: val, value: val }))}
                            onChange={(selected) => {
                              const values = selected
                                ? (
                                    selected as readonly { value: string }[]
                                  ).map((s) => s.value)
                                : [];
                              setFilterState((prev) => {
                                const next = { ...prev };
                                if (values.length > 0) {
                                  next["symbols"] = {
                                    type: "select",
                                    value: values,
                                  };
                                } else {
                                  delete next["symbols"];
                                }
                                return next;
                              });
                            }}
                          />
                          <X
                            className="w-4 h-4 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 cursor-pointer shrink-0"
                            onClick={() => setIsSymbolSearchOpen(false)}
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div
                      className={`flex items-center gap-1.5 w-full ${col.align === "right" ? "justify-end" : "justify-start"}`}
                    >
                      {col.label} {getSortIcon(sortConfig, col.key)}
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && stocks.length === 0 && <ScreenerTableSkeleton />}
            {stocks.map((row, idx) => {
              const isLastElement = stocks.length === idx + 1;
              const live = liveTicks[row.symbol];
              const basePrice = Number(row.price) || 0;
              // Prefer a live socket tick when we have one; otherwise fall back
              // to what the API already resolved from the market feed in the
              // database, so the price/change% are correct even when the
              // socket is disconnected or hasn't ticked for this symbol yet.
              const apiLtp =
                row.ltp !== null && row.ltp !== undefined
                  ? Number(row.ltp)
                  : basePrice;
              const apiChangePercent =
                row.change_percent !== null && row.change_percent !== undefined
                  ? Number(row.change_percent)
                  : null;

              const ltp = live ? live.ltp : apiLtp;
              const changePercent = live
                ? live.close
                  ? ((live.ltp - live.close) / live.close) * 100
                  : null
                : apiChangePercent;
              const hasChange = changePercent !== null;
              const changePercentValue = changePercent ?? 0;
              const isPositiveChange = hasChange
                ? changePercentValue === 0
                  ? null
                  : changePercentValue > 0
                : null;
              return (
                <tr
                  ref={isLastElement ? lastStockElementRef : null}
                  key={`${row.symbol}-${idx}`}
                  className={`border-b border-gray-100 dark:border-gray-800/50 hover:bg-blue-50/50 dark:hover:bg-[#1e2139] transition-colors`}
                >
                  <td className="px-2 pr-5 py-1.5 overflow-hidden">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="flex flex-col min-w-0">
                        <div className="font-semibold text-blue-600 dark:text-blue-400 truncate">
                          {row.symbol}
                        </div>
                        <div
                          className="text-[10px] text-gray-500 dark:text-gray-400 truncate"
                          title={row.company_name}
                        >
                          {row.company_name}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-2 pr-5 py-1.5 text-right whitespace-nowrap">
                    <FlashingValue
                      value={ltp ? formatCurrencyWithLocalString(ltp) : "-"}
                      isPositive={isPositiveChange}
                      flashTrigger={live?.lastUpdated}
                    />
                  </td>
                  <td
                    className={`px-2 pr-5 py-1.5 text-right whitespace-nowrap ${
                      isPositiveChange === true
                        ? "text-green-600 dark:text-green-400"
                        : isPositiveChange === false
                          ? "text-red-600 dark:text-red-400"
                          : "text-gray-700 dark:text-gray-300"
                    }`}
                  >
                    {hasChange
                      ? `${changePercentValue > 0 ? "+" : ""}${changePercentValue.toFixed(2)}%`
                      : "-"}
                  </td>
                  <td className="px-2 pr-5 py-1.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    {!row.volume || row.volume === "0" || row.volume === "0.00"
                      ? "-"
                      : row.volume}
                  </td>
                  <td className="px-2 pr-5 py-1.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    {Number(row.market_cap_in_cr)
                      ? formatToCr(Number(row.market_cap_in_cr) * 1e7)
                      : "-"}
                  </td>
                  <td className="px-2 pr-5 py-1.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    {Number(row.pe_ratio) ? row.pe_ratio : "-"}
                  </td>
                  <td
                    className={`px-2 pr-5 py-1.5 text-right whitespace-nowrap ${
                      Number(row.eps_dil_growth) > 0
                        ? "text-green-600 dark:text-green-400"
                        : Number(row.eps_dil_growth) < 0
                          ? "text-red-600 dark:text-red-400"
                          : "text-gray-700 dark:text-gray-300"
                    }`}
                  >
                    {Number(row.eps_dil_growth)
                      ? `${row.eps_dil_growth}%`
                      : "-"}
                  </td>
                  <td className="px-2 pr-5 py-1.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    {Number(row.dividend_yield)
                      ? `${row.dividend_yield}%`
                      : "-"}
                  </td>
                  <td className="px-2 pr-5 py-1.5 text-gray-700 dark:text-gray-300 overflow-hidden">
                    <span
                      className="block truncate"
                      title={row.sector || undefined}
                    >
                      {row.sector || "-"}
                    </span>
                  </td>
                  <td className="px-2 pr-5 py-1.5 overflow-hidden">
                    <span
                      className="block truncate text-gray-600 dark:text-gray-400"
                      title={row.industry || undefined}
                    >
                      {row.industry || "-"}
                    </span>
                  </td>
                  {/* <td className="px-3 py-1.5">
                    <span className="text-gray-600 dark:text-gray-400">
                      {row.index || "-"}
                    </span>
                  </td> */}
                </tr>
              );
            })}
            {loading && stocks.length > 0 && (
              <tr>
                <td
                  colSpan={SCREENER_TABLE_COLUMNS.length}
                  className="px-3 py-4 text-center text-sm text-gray-500"
                >
                  Loading...
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-800 flex justify-between items-center text-xs text-gray-500 dark:text-gray-400 bg-white dark:bg-[#111322]">
        <div>
          Showing {stocks.length} out of {totalItems} results
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {screenerToDelete && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
            onClick={() => setScreenerToDelete(null)}
          ></div>
          <div className="bg-white dark:bg-[#181a2d] border border-gray-200 dark:border-gray-800 rounded-xl p-6 w-full max-w-sm shadow-2xl relative transform transition-all">
            <div className="flex flex-col items-center text-center mb-6">
              <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
                <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-500" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                Delete Screener?
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Are you sure you want to delete the screener{" "}
                <span className="font-semibold text-gray-800 dark:text-gray-200">
                  &quot;{screenerToDelete.name}&quot;
                </span>
                ? This action cannot be undone.
              </p>
            </div>
            <div className="flex gap-3 w-full">
              <button
                onClick={() => setScreenerToDelete(null)}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={(e) => {
                  handleDeleteScreener(screenerToDelete.id, e);
                  setScreenerToDelete(null);
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors cursor-pointer shadow-lg shadow-red-500/20"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Load Modal with Search */}
      {isLoadDropdownOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
            onClick={() => {
              setIsLoadDropdownOpen(false);
              setSearchQuery("");
            }}
          ></div>
          <div className="bg-white dark:bg-[#181a2d] border border-gray-200 dark:border-gray-800 rounded-xl w-full max-w-md shadow-2xl relative transform transition-all flex flex-col max-h-[600px]">
            {/* Header */}
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center bg-gray-50/50 dark:bg-white/5 rounded-t-xl">
              <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <FolderOpen className="w-5 h-5 text-blue-500" />
                Load Screener
              </h3>
              <button
                onClick={() => {
                  setIsLoadDropdownOpen(false);
                  setSearchQuery("");
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Search Bar */}
            <div className="p-4 border-b border-gray-100 dark:border-gray-800/50">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search screeners..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-black/20 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all placeholder:text-gray-400 dark:placeholder:text-gray-600"
                />
              </div>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto p-2">
              {isFetchingScreeners ? (
                <div className="p-8 flex justify-center text-gray-400">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : savedScreeners.length === 0 ? (
                <div className="p-8 text-sm text-center text-gray-500 dark:text-gray-400 flex flex-col items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                    <FolderOpen className="w-6 h-6 opacity-40" />
                  </div>
                  No saved screeners found.
                </div>
              ) : savedScreeners.filter((s) =>
                  (s.name || "")
                    .toLowerCase()
                    .includes(searchQuery.toLowerCase()),
                ).length === 0 ? (
                <div className="p-8 text-sm text-center text-gray-500 dark:text-gray-400 flex flex-col items-center gap-2">
                  No results found for &quot;{searchQuery}&quot;
                </div>
              ) : (
                <div className="space-y-0.5">
                  {savedScreeners
                    .filter((s) =>
                      (s.name || "")
                        .toLowerCase()
                        .includes(searchQuery.toLowerCase()),
                    )
                    .map((screener, index) => {
                      const isSelected = activeScreener?.id === screener.id;
                      return (
                        <div
                          key={screener.id}
                          className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition-colors ${isSelected ? "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400" : "hover:bg-gray-50 dark:hover:bg-gray-800/50 text-gray-700 dark:text-gray-300"} group`}
                          onClick={() => {
                            handleLoadScreener(screener);
                            setSearchQuery("");
                          }}
                        >
                          <div className="flex items-center gap-2 overflow-hidden mr-2">
                            <span className="text-gray-400 dark:text-gray-500 font-mono text-[11px] w-4 text-right shrink-0">
                              {index + 1}.
                            </span>
                            {isSelected ? (
                              <Check className="w-4 h-4 shrink-0 text-blue-600 dark:text-blue-400" />
                            ) : (
                              <div className="w-4 h-4 shrink-0" />
                            )}
                            <span className="text-sm truncate">
                              {screener.name}
                            </span>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setScreenerToDelete({
                                id: screener.id,
                                name: screener.name,
                              });
                            }}
                            className="text-gray-400 hover:text-red-500 p-1.5 hover:bg-red-50 dark:hover:bg-red-900/40 rounded cursor-pointer shrink-0"
                            title="Delete screener"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Improved Save Modal */}
      {isSaveModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
            onClick={() => setIsSaveModalOpen(false)}
          ></div>
          <div className="bg-white dark:bg-[#181a2d] border border-gray-200 dark:border-gray-800 rounded-xl w-full max-w-sm shadow-2xl relative transform transition-all overflow-hidden">
            {/* Modal Header */}
            <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-white dark:from-blue-900/20 dark:to-[#181a2d] border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
              <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Save className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                Save Screener
              </h3>
              <button
                onClick={() => setIsSaveModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Screener Name
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={screenerName}
                    onChange={(e) => setScreenerName(e.target.value)}
                    className="w-full pl-2 pr-4 py-2.5 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-black/20 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all placeholder:text-gray-400 dark:placeholder:text-gray-600"
                    placeholder="e.g., High Volume Tech"
                    autoFocus
                    onKeyDown={(e) => {
                      if (
                        e.key === "Enter" &&
                        !isSaving &&
                        screenerName.trim()
                      ) {
                        handleSaveScreener();
                      }
                    }}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setIsSaveModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveScreener}
                  disabled={isSaving || !screenerName.trim()}
                  className="px-5 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:hover:bg-blue-600 transition-colors cursor-pointer flex items-center gap-2 shadow-lg shadow-blue-500/20 disabled:cursor-not-allowed"
                >
                  {isSaving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Discard Changes Modal */}
      {showDiscardModal && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
            onClick={() => setShowDiscardModal(false)}
          ></div>
          <div className="bg-white dark:bg-[#181a2d] border border-gray-200 dark:border-gray-800 rounded-xl p-6 w-full max-w-sm shadow-2xl relative transform transition-all">
            <div className="flex flex-col items-center text-center mb-6">
              <div className="w-12 h-12 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center mb-4">
                <AlertTriangle className="w-6 h-6 text-orange-600 dark:text-orange-500" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                Unsaved Changes
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                You have unsaved changes in{" "}
                <span className="font-semibold text-gray-800 dark:text-gray-200 break-all">
                  &quot;{activeScreener?.name}&quot;
                </span>
                . Are you sure you want to close it? All modifications will be
                lost.
              </p>
            </div>
            <div className="flex gap-3 w-full">
              <button
                onClick={() => setShowDiscardModal(false)}
                className="cursor-pointer flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowDiscardModal(false);
                  setActiveScreener(null);
                  setFilterState({});
                  setSortConfig({ key: null, direction: null });
                }}
                className="cursor-pointer flex-1 px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors"
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
